import {
  Button,
  ButtonIcon,
  InputSearch,
  Select,
  Text,
  View
} from "@/components";

import useTable from "@/hooks/useTable";

import type { JSX } from "react";
import type { TableData } from "./types";

/**
 * @function TableComplex
 * @description Sortable, paginated, searchable data table. Wraps `useTable`
 * for client-side sort/paginate/search state and renders the resulting page
 * of rows, deriving its columns from the keys of the first data record
 * (minus `hiddenCols`).
 *
 * @template T - Row shape. Must be a flat record of string values; a value
 * of `keyName` or an array value is delegated to `Component` instead of
 * being rendered as plain text (see `renderCell`).
 *
 * @param {TableData<T>} props
 * @param {string} [props.title] - Heading shown above the table.
 * @param {T[]} props.data - Full, unpaginated dataset.
 * @param {string[]} [props.sortBy] - Column keys offered in the "sort by" dropdown. Omit/empty to hide it.
 * @param {string[]} [props.hiddenCols] - Column keys present in `data` that should not get a column.
 * @param {TableAction[]} [props.actions] - Row actions (icon buttons) rendered in a trailing column.
 * @param {string} [props.keyName] - Column key whose cell should render via `Component` instead of as text (e.g. an avatar/name cell).
 * @param {React.ComponentType} [props.Component] - Renderer used for `keyName` cells and for array-valued cells; defaults to plain `<Text>`.
 * @returns {JSX.Element} The rendered table, with pagination controls shown once `data` exceeds one page.
 */
const TableComplex = <T extends Record<string, string>>({
  title,
  data,
  hiddenCols = [],
  actions,
  keyName = "",
  Component = ({ label }: Record<string, unknown>) => (
    <Text>{label as string}</Text>
  )
}: TableData<T>): JSX.Element => {
  const {
    paginate,
    sortData,
    totalPages,
    startIndex,
    endIndex,
    totalData,
    tableData
  } = useTable<T>(data, 10);
  const cols = Object.keys(tableData[0] || {}).filter(
    (col: string) => !hiddenCols?.includes(col)
  );

  /**
   * @function renderCell
   * @description Resolves how a single cell renders: an array value maps
   * each item through `Component`, the `keyName` column renders the whole
   * `record` through `Component`, and everything else renders as plain text.
   *
   * @param {string} key - Column key being rendered.
   * @param {T} record - Row this cell belongs to.
   * @returns {JSX.Element | JSX.Element[]} The cell content.
   */
  const renderCell = (key: string, record: T): JSX.Element | JSX.Element[] => {
    const value: string = record[key] || "";

    if (Array.isArray(value)) {
      return value.map((item: T) => <Component key={item.id} {...item} />);
    }

    if (keyName === key) {
      return <Component key={record.id} {...record} />;
    }

    return <Text key={record.id}>{value}</Text>;
  };

  return (
    <>
      <View className="flex flex-wrap gap-3 justify-between items-center bb-dashed">
        <Text type="p" className="font-medium">
          {title}
        </Text>
        <View className="flex items-center gap-4 lg:gap-8 xl:gap-10">
          <InputSearch
            name="input-search"
            placeholder="Buscar ..."
            onSearch={() => alert("Buscar")}
          />
          <View className="flex items-center shrink-0 gap-2">
            {cols.length > 0 && (
              <Select
                items={cols.map((item: string) => ({
                  id: item,
                  label: item,
                  value: item
                }))}
                defaultValue={cols[0]}
                selected={cols[0]}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                  sortData(e.target.value as keyof T)
                }
              >
                <Text type="p" className="text-xs sm:text-sm">
                  Sort by
                </Text>
              </Select>
            )}
          </View>
        </View>
      </View>
      <View className="rounded-lg border overflow-auto flex flex-col justify-center scrollbar-thin">
        <View
          type="table"
          className="w-full whitespace-nowrap border-separate border-spacing-0"
        >
          <View type="thead" className="sticky top-0 z-20">
            <View type="tr" className="bg-primary/5 dark:bg-bg3 font-semibold">
              {cols.map((col: string) => (
                <View type="td" className="p-5 pl-6 border-b" key={col}>
                  <Text className="flex items-center gap-1 capitalize">
                    {col}
                  </Text>
                </View>
              ))}
              {actions && actions?.length > 0 && (
                <View type="td" className="p-5 text-center border-b">
                  <Text type="p">Actions</Text>
                </View>
              )}
            </View>
          </View>
          <View type="tbody">
            {cols?.map((_, id: number) => (
              <View type="tr" key={tableData[id].id}>
                {cols.map((col: string) => (
                  <View type="td" key={col}>
                    {renderCell(col, tableData[id])}
                  </View>
                ))}
                {actions?.length && (
                  <View type="td" className="flex justify-center">
                    {actions.map(({ action, disabled, icon, label }) => (
                      <ButtonIcon
                        key={`action_${tableData[id].id}_${label}`}
                        iconLeft={{ name: icon }}
                        aria-label={label}
                        disabled={disabled}
                        action={() => action(tableData[id].id)}
                      />
                    ))}
                  </View>
                )}
              </View>
            ))}
          </View>
        </View>
      </View>
      {totalData > 10 && (
        <View className="flex col-span-12 gap-4 sm:justify-between justify-center items-center flex-wrap">
          <Text type="p">
            {startIndex + 1} - {endIndex + 1} / {totalData}
          </Text>
          <View
            type="ul"
            className="flex gap-2 md:gap-3 flex-wrap md:font-semibold items-center"
          >
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <View type="li" key={`page_${page}`}>
                <Button
                  action={() => paginate(page)}
                  className="hover:bg-primary disabled:hover:bg-transparent disabled:hover:text-primary text-primary rtl:rotate-180 disabled:cursor-not-allowed hover:text-n0 border md:w-10 duration-300 md:h-10 w-8 h-8 flex items-center rounded-full justify-center border-primary"
                >
                  {page}
                </Button>
              </View>
            ))}
          </View>
        </View>
      )}
    </>
  );
};

export default TableComplex;
